const USER_KEY = "user";
const TOKEN_KEY = "token";

export interface AuthUser {
  id: string | number;
  account: string;
  nickname?: string;
  phone?: string;
  position?: string;
  enterprise_id?: string | number;
  department_id?: string | number;
  token?: string;
  enterprise?: {
    id?: string | number;
    title?: string;
    address?: string;
    /** 最迟退货天数（完成时间起算） */
    latest?: number | string;
    [key: string]: unknown;
  };
  department?: {
    id?: string | number;
    title?: string;
    /** 是否启用采购额度/计划限制 */
    is_limit_quota?: boolean | number | string;
    purchase_plan_type?: number | string;
    /** 额度上限（年度 config / 当月计划金额） */
    quota?: number | string;
    /** 已使用额度 */
    used_amount?: number | string;
    /** 剩余额度 */
    remain_amount?: number | string;
    /** 剩余比率（%），已使用比率 = 100 - remain_ratio */
    remain_ratio?: number | string;
    [key: string]: unknown;
  };
  is_canedit?: boolean | number | string;
  /** 是否可跨科室查看/筛选（为 true 时显示科室下拉） */
  is_divide?: boolean | number | string;
  /** 是否采购计划管理员（可创建全院限购） */
  is_purchase_plan_manager?: boolean | number | string;
  [key: string]: unknown;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getUser<T = AuthUser>(): T | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function setUser(user: AuthUser) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (user.token) {
    setToken(String(user.token));
  }
}

export function isLoggedIn(): boolean {
  return !!getUser();
}

/** 是否代采账号（可跨科室 / 代购下单） */
export function isDivideAccount(user: AuthUser | null | undefined = getUser()): boolean {
  const flag = user?.is_divide;
  return flag === true || flag === 1 || flag === '1';
}

/** 是否采购计划管理员（可选择全院限购） */
export function isPurchasePlanManager(user: AuthUser | null | undefined = getUser()): boolean {
  const flag = user?.is_purchase_plan_manager;
  return flag === true || flag === 1 || flag === '1';
}

/** 当前账号所属科室是否开启采购计划额度限制 */
export function isLimitQuotaDepartment(user: AuthUser | null | undefined = getUser()): boolean {
  const flag = user?.department?.is_limit_quota;
  return flag === true || flag === 1 || flag === '1';
}

/** 科室采购额度信息（来自 /platform/auth/member 的 department） */
export interface DepartmentQuotaInfo {
  quota: number;
  usedAmount: number;
  remainAmount: number;
  /** 剩余比率（%）— 接口字段 remain_ratio */
  remainRatio: number;
  /** 已使用比率（%）— 由 remain_ratio 换算，或 used/quota */
  usedRatio: number;
}

/**
 * 读取科室采购额度；未开启采购控制时返回 null
 */
export function getDepartmentQuota(
  user: AuthUser | null | undefined = getUser(),
): DepartmentQuotaInfo | null {
  if (!isLimitQuotaDepartment(user) || !user?.department) return null;
  const dept = user.department;
  const quota = Number(dept.quota);
  const usedAmount = Number(dept.used_amount);
  const remainAmount = Number(dept.remain_amount);
  const remainRatioRaw = Number(dept.remain_ratio);

  const safeQuota = Number.isFinite(quota) ? quota : 0;
  const safeUsed = Number.isFinite(usedAmount) ? usedAmount : 0;
  const safeRemain = Number.isFinite(remainAmount) ? remainAmount : 0;
  const remainRatio = Number.isFinite(remainRatioRaw)
    ? remainRatioRaw
    : safeQuota > 0
      ? (safeRemain / safeQuota) * 100
      : 0;
  const usedRatio = Number.isFinite(remainRatioRaw)
    ? Math.max(0, 100 - remainRatioRaw)
    : safeQuota > 0
      ? (safeUsed / safeQuota) * 100
      : 0;

  return {
    quota: safeQuota,
    usedAmount: safeUsed,
    remainAmount: safeRemain,
    remainRatio,
    usedRatio,
  };
}

/**
 * 是否可访问采购计划菜单/页面
 * 条件：当前科室 is_limit_quota = true（具体控制看当月 wz_flow_items）
 */
export function canAccessPurchasePlan(user: AuthUser | null | undefined = getUser()): boolean {
  return isLimitQuotaDepartment(user);
}

export function clearAuth() {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

/** 强制跳转登录页（清除登录缓存） */
export function redirectToLogin(options?: { replaceHistory?: boolean }) {
  clearAuth();
  const replaceHistory = options?.replaceHistory ?? false;

  if (replaceHistory) {
    const base = (__BASE_PATH__ || "/").replace(/\/$/, "");
    // replace 掉当前历史记录，降低回退到已退出页面的可能
    window.location.replace(`${base}/login`);
    return;
  }

  if (typeof window.REACT_APP_NAVIGATE === "function") {
    window.REACT_APP_NAVIGATE("/login", { replace: true });
    return;
  }
  const base = (__BASE_PATH__ || "/").replace(/\/$/, "");
  window.location.href = `${base}/login`;
}
